import { BaseModuleProps, CmsModuleData } from '@activebrands/core-web/types/modules';
import { logger } from '@activebrands/core-web/utils/logger';
import loadable from '@loadable/component';
import ErrorCatcher from 'components/ErrorCatcher';

interface ModulesProps extends BaseModuleProps {
    modules?: CmsModuleData[];
}

const availableModules = {
    accordionModule: loadable(() => import('@activebrands/core-web/components/modules/AccordionModule')),
    accountShortcutsModule: loadable(() => import('libs/storyblok/content/Modules/AccountShortcutsModule')),
    actRewardsSummaryModule: loadable(
        () => import('@activebrands/core-web/components/modules/ActRewardsSummaryModule')
    ),
    articlePromotionModule: loadable(() => import('@activebrands/core-web/components/modules/ArticlePromotionModule')),
    backgroundMediaModule: loadable(() => import('@activebrands/core-web/components/modules/BackgroundMediaModule')),
    buttonsListModule: loadable(() => import('@activebrands/core-web/components/modules/ButtonsListModule')),
    callToActionModule: loadable(() => import('@activebrands/core-web/components/modules/CallToActionModule')),
    categoryModule: loadable(() => import('@activebrands/core-web/components/modules/CategoryModule')),
    discoverModule: loadable(() => import('@activebrands/core-web/components/modules/DiscoverModule')),
    featurePromotionModule: loadable(() => import('@activebrands/core-web/components/modules/FeaturePromotionModule')),
    flexibleModule: loadable(() => import('@activebrands/core-web/components/modules/FlexibleModule')),
    fourColumnsModule: loadable(() => import('libs/storyblok/content/Modules/FourColumnsModule')),
    highlightModule: loadable(() => import('@activebrands/core-web/components/modules/HighlightModule')),
    informationModule: loadable(() => import('@activebrands/core-web/components/modules/InformationModule')),
    loopingTextBannerModule: loadable(() => import('libs/storyblok/content/Modules/LoopingTextBannerModule')),
    newsletterModule: loadable(() => import('libs/storyblok/content/Modules/NewsletterModule')),
    productPromotionModule: loadable(() => import('libs/storyblok/content/Modules/ProductPromotionModule')),
    returnIframeModule: loadable(() => import('@activebrands/core-web/components/modules/ReturnIframeModule')),
    shopTheLookModule: loadable(() => import('@activebrands/core-web/components/modules/ShopTheLookModule')),
    textModule: loadable(() => import('@activebrands/core-web/components/modules/TextModule')),
    threeColumnsModule: loadable(() => import('libs/storyblok/content/Modules/ThreeColumnsModule')),
    twoColumnsModule: loadable(() => import('libs/storyblok/content/Modules/TwoColumnsModule')),
    videoPlayerModule: loadable(() => import('@activebrands/core-web/components/modules/VideoPlayerModule')),
};

type AvailableModuleName = keyof typeof availableModules;

const getModuleComponent = (componentName?: string) => {
    if (!componentName || !(componentName in availableModules)) {
        return null;
    }

    return availableModules[componentName as AvailableModuleName];
};

const Modules = ({ modules = [], ...rest }: ModulesProps) => {
    if (modules && modules.length > 0) {
        return modules.map((moduleData, i) => {
            const componentName = moduleData.component;
            const ContentComponent = getModuleComponent(componentName);

            if (ContentComponent) {
                return (
                    <ErrorCatcher key={moduleData._uid}>
                        <ContentComponent content={moduleData} index={i} layout={componentName} {...rest} />
                    </ErrorCatcher>
                );
            }

            logger.warn(`ContentComponent ${componentName} not found.`);
            return null;
        });
    }
    return null;
};

export default Modules;
